import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type {
  RealtimeConfig,
  RealtimeConnectionState,
  RealtimeContextState,
  RealtimeFeatureFlags,
} from '@hetivo/hetivo-lib-types';
import { useApiClient } from '../api/api-client-context';
import { WebPubSubService } from './web-pubsub-service';

interface RealtimeProviderProps {
  /** Name of the API client instance to use for negotiate calls (e.g., 'admin-auth', 'deliveries') */
  apiClientName: string;
  /** Whether to auto-connect when the provider mounts (default: true when enabled) */
  autoConnect?: boolean;
  children: React.ReactNode;
  /** Realtime configuration. If not provided, realtime is disabled. */
  config?: RealtimeConfig;
  /** Master enable/disable flag (default: false — opt-in) */
  enabled?: boolean;
  /** Per-resource feature flags */
  featureFlags?: RealtimeFeatureFlags;
  /** Tenant ID for multi-tenant isolation */
  tenantId?: string;
  /** User ID for the connection */
  userId?: string;
}

type RealtimeContextValue = RealtimeContextState & {
  featureFlags: RealtimeFeatureFlags;
  service: WebPubSubService;
};

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function RealtimeProvider({
  apiClientName,
  autoConnect = true,
  children,
  config,
  enabled = false,
  featureFlags = {},
  tenantId,
  userId,
}: RealtimeProviderProps): React.JSX.Element {
  const apiClient = useApiClient(apiClientName);
  const serviceRef = useRef<WebPubSubService>(new WebPubSubService());
  const [connectionState, setConnectionState] = useState<RealtimeConnectionState>('disconnected');
  const [connectionError, setConnectionError] = useState<Error | null>(null);

  useEffect(() => {
    const service = serviceRef.current;
    const unsubscribe = service.onConnectionStateChanged((state, error) => {
      setConnectionState(state);
      setConnectionError(error ?? null);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const service = serviceRef.current;

    const shouldConnect =
      enabled && autoConnect && !!config && !!tenantId && !!userId && connectionState === 'disconnected';

    if (!shouldConnect) return;

    service.connect(config, apiClient.instance, tenantId, userId).catch((error: unknown) => {
      console.error('[RealtimeProvider] Connection error:', error);
    });
  }, [enabled, autoConnect, config, tenantId, userId, apiClient, connectionState]);

  useEffect(() => {
    const service = serviceRef.current;
    return (): void => {
      service.disconnect();
    };
  }, []);

  const connect = useCallback(async (): Promise<void> => {
    if (!config || !tenantId || !userId) return;
    await serviceRef.current.connect(config, apiClient.instance, tenantId, userId);
  }, [config, tenantId, userId, apiClient]);

  const disconnect = useCallback((): void => {
    serviceRef.current.disconnect();
  }, []);

  const isConnected = connectionState === 'connected';

  const value = useMemo<RealtimeContextValue>(
    () => ({
      connect,
      disconnect,
      error: connectionError,
      featureFlags,
      isConnected,
      isEnabled: enabled,
      service: serviceRef.current,
      state: connectionState,
    }),
    [connect, disconnect, connectionError, featureFlags, isConnected, enabled, connectionState],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

/**
 * Hook to access the realtime context.
 * Returns a safe no-op object if used outside RealtimeProvider (never errors).
 */
export function useRealtimeContext(): RealtimeContextState & { featureFlags: RealtimeFeatureFlags } {
  const context = useContext(RealtimeContext);

  if (!context) {
    return {
      connect: async (): Promise<void> => {},
      disconnect: (): void => {},
      error: null,
      featureFlags: {},
      isConnected: false,
      isEnabled: false,
      state: 'disconnected' as const,
    };
  }

  return context;
}

/**
 * Internal hook that returns the full context including the service reference.
 * Not exported — use useRealtimeContext() for public API.
 */
export function useInternalRealtimeContext(): RealtimeContextValue | null {
  return useContext(RealtimeContext);
}
